/**
 * The division -> department tree behind the Audience section: which departments to offer for the
 * divisions already picked, what to drop when the divisions change, and how the picks become the
 * rules the server stores. (Sub-department targeting was removed on 30 Oct 2026: part of a department
 * is reached by combining it with a country or a level.)
 *
 * The engine ORs the org includes (AudienceMatcher.php:41-45): "Operations" + "Engineering" reaches
 * all of Operations. On screen a publisher picks a division and then narrows it to departments, so
 * the save keeps only the departments under a narrowed division (narrowOrg). On edit the divisions
 * of the stored departments are shown again (withParents), so the screen looks as it did when saved.
 */

const ids = (map, mode) => Object.keys(map || {}).filter((id) => map[id] === mode);
const divisionOf = (departments, id) => {
  const hit = (departments || []).find((d) => String(d.id) === String(id));
  return hit && hit.division_id !== null && hit.division_id !== undefined ? String(hit.division_id) : null;
};

// The departments to offer for the division picks so far (non-HR callers never pick divisions).
export const departmentPool = (org, lookups, isHr) => {
  const incDiv = isHr ? ids(org.division, "include") : [];
  const excDiv = isHr ? ids(org.division, "exclude") : [];
  let pool = lookups.departments || [];
  if (incDiv.length) pool = pool.filter((d) => d.division_id !== null && incDiv.includes(String(d.division_id)));
  if (excDiv.length) pool = pool.filter((d) => d.division_id === null || !excDiv.includes(String(d.division_id)));
  return pool;
};

// Drops department picks no longer on offer after the divisions changed; returns the org and the count.
export const pruneOrg = (org, lookups, isHr) => {
  const allowed = departmentPool(org, lookups, isHr).map((d) => String(d.id));
  const kept = {};
  let removed = 0;
  Object.keys(org.department || {}).forEach((id) => {
    if (!org.department[id]) return;
    if (allowed.includes(id)) kept[id] = org.department[id];
    else removed += 1;
  });
  return { org: { ...org, department: kept }, removed };
};

// The org picks as stored: a division include is dropped when a department include sits under it.
export const narrowOrg = (org, lookups) => {
  const narrowed = new Set(ids(org.department, "include").map((id) => divisionOf(lookups.departments, id)).filter(Boolean));
  const division = {};
  Object.keys(org.division || {}).forEach((id) => {
    if (org.division[id] === "include" && narrowed.has(id)) return;
    division[id] = org.division[id];
  });
  return { division, department: { ...(org.department || {}) } };
};

// The stored picks with the divisions of included departments ticked again, for HR's edit screen.
export const withParents = (org, lookups, isHr) => {
  const next = { division: { ...org.division }, department: { ...org.department } };
  if (!isHr) return next;
  const known = (lookups.divisions || []).map((d) => String(d.id));
  ids(org.department, "include").forEach((id) => {
    const parent = divisionOf(lookups.departments, id);
    if (parent && known.includes(parent) && !next.division[parent]) next.division[parent] = "include";
  });
  return next;
};

// One line in plain words: who will see the item.
export const audienceSummary = (values, lookups) => {
  if (values.company_wide) return "Everyone in EVOX.";
  const name = (list, id) => {
    const hit = (list || []).find((x) => String(x.id) === String(id));
    return hit ? hit.name : "#" + id;
  };
  const parts = [];

  const countries = Object.keys(values.countries || {}).filter((id) => values.countries[id]);
  parts.push(countries.length ? countries.map((id) => name(lookups.countries, id)).join(", ") : "All countries");

  const narrowed = narrowOrg(values.org, lookups);
  const inc = [];
  const exc = [];
  [["division", lookups.divisions], ["department", lookups.departments]].forEach(([type, list]) => {
    ids(narrowed[type], "include").forEach((id) => inc.push(name(list, id)));
    ids(narrowed[type], "exclude").forEach((id) => exc.push(name(list, id)));
  });
  parts.push(inc.length ? inc.join(", ") : "all departments");
  if (exc.length) parts.push("except " + exc.join(", "));

  const incLevels = ids(values.levels, "include").map((id) => name(lookups.levels, id));
  const excLevels = ids(values.levels, "exclude").map((id) => name(lookups.levels, id));
  if (incLevels.length) parts.push("levels: " + incLevels.join(", "));
  if (excLevels.length) parts.push("not " + excLevels.join(", "));

  if (values.hire_min !== "" || values.hire_max !== "") {
    const min = values.hire_min === "" ? 0 : values.hire_min;
    parts.push(values.hire_max === "" ? "hired " + min + "+ days ago" : "hired " + min + "–" + values.hire_max + " days ago");
  }
  (lookups.conditions || []).forEach((c) => {
    if (values.conditions && values.conditions[c.key]) parts.push(c.label.toLowerCase());
  });
  return parts.join(" · ") + ".";
};
